/**
 * Workspace color utilities
 * Auto-assigns consistent colors to workspaces based on their name
 * Uses Microsoft 365-style soft/pastel colors
 */

/**
 * Microsoft 365-inspired soft color palette
 * These are muted, non-primary colors that work well as backgrounds
 */
export const WORKSPACE_COLORS = [
  '#6BB9F0', // Soft Blue
  '#4ECDC4', // Soft Teal
  '#7CB97F', // Soft Green
  '#9B89B3', // Soft Purple
  '#D4A5A5', // Soft Pink
  '#F5A962', // Soft Orange
  '#F7DC6F', // Soft Yellow
  '#E08283', // Soft Red
  '#F08080', // Soft Coral
  '#7986CB', // Soft Indigo
] as const;

/**
 * Generate a consistent hash from a string
 * Given the same string, always returns the same number
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Get a consistent color for a workspace based on its name or ID
 * The same workspace always gets the same color
 */
export function getWorkspaceColor(workspaceIdentifier: string): string {
  const hash = hashString(workspaceIdentifier);
  const colorIndex = hash % WORKSPACE_COLORS.length;
  return WORKSPACE_COLORS[colorIndex];
}

/**
 * Get a two-letter abbreviation for a workspace name
 * Uses first letters of first two words, or first two letters if single word
 */
export function getWorkspaceAbbreviation(name: string): string {
  const words = name.trim().split(/\s+/);

  if (words.length >= 2) {
    // First letter of first two words
    return (words[0][0] + words[1][0]).toUpperCase();
  } else if (words[0].length >= 2) {
    // First two letters of single word
    return words[0].substring(0, 2).toUpperCase();
  } else {
    // Single character name
    return words[0].toUpperCase();
  }
}

/**
 * Convert a hex color to rgba with specified opacity
 * Used for selected state backgrounds
 */
export function hexToRgba(hex: string, opacity: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return `rgba(0, 0, 0, ${opacity})`;
  }
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}
